import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import TraderModal from "./features/traders/components/TraderModal";
import { useAuth } from "./authContext";

function hasTradeFinderLocation(user) {
  return Boolean(
    user
    && String(user.locationLabel || "").trim()
    && Number.isFinite(Number(user.locationLat))
    && Number.isFinite(Number(user.locationLng))
  );
}

function getLocationPromptStorageKey(userId) {
  return `tradefinder-location-prompt-dismissed:${String(userId || "anonymous")}`;
}

export default function ProtectedRoute({ children, requireTradeFinderLocation = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [isLocationPromptOpen, setIsLocationPromptOpen] = useState(false);
  const hasLocation = useMemo(() => hasTradeFinderLocation(user), [user]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || hasLocation || location.pathname === "/account/tradefinder-settings") {
      setIsLocationPromptOpen(false);
      return;
    }

    const storageKey = getLocationPromptStorageKey(user.id);

    if (window.localStorage.getItem(storageKey) === "dismissed") {
      setIsLocationPromptOpen(false);
      return;
    }

    setIsLocationPromptOpen(true);
  }, [hasLocation, isAuthenticated, location.pathname, user?.id]);

  useEffect(() => {
    if (!user?.id || !hasLocation) {
      return;
    }

    window.localStorage.removeItem(getLocationPromptStorageKey(user.id));
  }, [hasLocation, user?.id]);

  const handleDismissLocationPrompt = () => {
    if (user?.id) {
      window.localStorage.setItem(getLocationPromptStorageKey(user.id), "dismissed");
    }

    setIsLocationPromptOpen(false);
  };

  const handleOpenTradeFinderSettings = () => {
    setIsLocationPromptOpen(false);
    navigate("/account/tradefinder-settings");
  };

  if (isLoading) {
    return (
      <div className="account-shell">
        <div className="account-loading">Checking account session...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (isAuthenticated && requireTradeFinderLocation && !hasLocation) {
      return (
        <div className="account-shell">
          <section className="account-settings-panel">
            <div className="account-page-header">
              <div>
                <h1 className="account-page-title">Set Your TradeFinder Location</h1>
                <p className="account-copy">
                  TradeFinder needs your city or area before it can calculate local matches.
                </p>
              </div>
            </div>
            <div className="account-setting-card" style={{ display: "grid", gap: "16px" }}>
              <p className="account-copy" style={{ margin: 0 }}>
                Add a location and radius in TradeFinder Settings, then come back to continue.
              </p>
              <div className="account-save-row" style={{ justifyContent: "flex-start" }}>
                <Link to="/account/tradefinder-settings" className="account-action-button account-action-button--location-cta">
                  Open TradeFinder Settings
                </Link>
              </div>
            </div>
          </section>
        </div>
      );
    }

    return (
      <>
        {children}
        <TraderModal isOpen={isLocationPromptOpen} onClose={handleDismissLocationPrompt} title="Complete Your TradeFinder Location" width="560px">
          <div style={{ display: "grid", gap: "16px" }}>
            <p style={{ margin: 0, color: "#334155", lineHeight: 1.6 }}>
              Set your city or area and choose a trade radius to unlock local TradeFinder matches.
            </p>
            <p style={{ margin: 0, color: "#64748b", lineHeight: 1.6 }}>
              You can do it now in TradeFinder Settings, or skip it and come back later.
            </p>
            <div className="account-location-prompt__actions">
              <button type="button" className="account-action-button account-location-prompt__button account-location-prompt__button--secondary" onClick={handleDismissLocationPrompt}>
                Maybe later
              </button>
              <button type="button" className="account-action-button account-location-prompt__button account-location-prompt__button--primary" onClick={handleOpenTradeFinderSettings}>
                Open TradeFinder Settings
              </button>
            </div>
          </div>
        </TraderModal>
      </>
    );
  }

  return <Navigate to="/auth" replace state={{ from: location }} />;
}